import { z } from "zod";
import { CandidateStatusSchema, StageSchema } from "./enums.js";

/** A candidate / person. Maps to the Phase-2 "Person" entity (one row per human). */
export const CandidateSchema = z.object({
  id: z.uuid(),
  fullName: z.string(),
  aliases: z.array(z.string()),
  currentEmployer: z.string().nullable(),
  currentTitle: z.string().nullable(),
  location: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  currentSalary: z.number().nullable(),
  totalComp: z.number().nullable(),
  salaryExpectation: z.number().nullable(),
  currency: z.string(),
  noticePeriodDays: z.number().int().nullable(),
  earliestStart: z.string().nullable(),
  source: z.string().nullable(),
  strengths: z.string().nullable(),
  watchouts: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Candidate = z.infer<typeof CandidateSchema>;

export const CandidateCreateSchema = z.object({
  fullName: z.string().min(1),
  aliases: z.array(z.string()).default([]),
  currentEmployer: z.string().nullish(),
  currentTitle: z.string().nullish(),
  location: z.string().nullish(),
  email: z.string().email().nullish(),
  phone: z.string().nullish(),
  currentSalary: z.number().nonnegative().nullish(),
  totalComp: z.number().nonnegative().nullish(),
  salaryExpectation: z.number().nonnegative().nullish(),
  currency: z.string().default("USD"),
  noticePeriodDays: z.number().int().nonnegative().nullish(),
  earliestStart: z.iso.date().nullish(),
  source: z.string().nullish(),
  strengths: z.string().nullish(),
  watchouts: z.string().nullish(),
});
export type CandidateCreate = z.infer<typeof CandidateCreateSchema>;

export const CandidateUpdateSchema = CandidateCreateSchema.partial();
export type CandidateUpdate = z.infer<typeof CandidateUpdateSchema>;

/** The candidate↔search edge — pipeline state lives here, not on the person. */
export const CandidateSearchSchema = z.object({
  id: z.uuid(),
  candidateId: z.uuid(),
  searchId: z.uuid(),
  stage: StageSchema,
  status: CandidateStatusSchema,
  stageEnteredAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CandidateSearch = z.infer<typeof CandidateSearchSchema>;

export const CandidateLinkSchema = z.object({
  searchId: z.uuid(),
  stage: StageSchema.default("longlist"),
  status: CandidateStatusSchema.default("active"),
});
export type CandidateLink = z.infer<typeof CandidateLinkSchema>;

/** Quick-add (PRD §7.4): Name + Stage + optional search to attach to. */
export const QuickAddSchema = z.object({
  fullName: z.string().min(1),
  searchId: z.uuid(),
  stage: StageSchema.default("longlist"),
});
export type QuickAdd = z.infer<typeof QuickAddSchema>;
